/**
 * Edge Function Client Wrapper
 * Handles authentication and calls to protected Edge Functions
 * Part of Phase 1 security fix #859
 */

import { logger } from "./logger";
import { getSupabaseSessionWithTimeout } from "./supabaseApi";
import { getGuestOwnerToken } from "./guestIdentityService";
import { getEnv } from "./env";

const EDGE_FUNCTION_BASE = (() => {
  // IMPORTANT : utiliser getEnv (import.meta.env) et NON process.env.
  // Dans le bundle navigateur de prod, process.env.VITE_SUPABASE_URL est
  // undefined → fallback sur window.location.origin → les appels partent vers
  // https://doodates.com/functions/v1/... (Vercel) au lieu de Supabase → 405.
  const supabaseUrl = getEnv("VITE_SUPABASE_URL");
  if (supabaseUrl) {
    return `${supabaseUrl}/functions/v1`;
  }
  // Fallback: use client-side window.location to access current host
  if (typeof window !== "undefined") {
    return `${window.location.origin}/functions/v1`;
  }
  throw new Error("VITE_SUPABASE_URL not configured and window not available");
})();

async function getAuthToken(): Promise<string | null> {
  try {
    const session = await getSupabaseSessionWithTimeout(2000);
    return session?.access_token || null;
  } catch (error) {
    logger.warn("Failed to get auth token for Edge Function", "api", {
      error,
    });
    return null;
  }
}

async function callEdgeFunction<T>(
  functionName: string,
  body: unknown,
): Promise<T> {
  const token = await getAuthToken();
  if (!token) {
    throw new Error("Authentication required for this operation");
  }

  const url = `${EDGE_FUNCTION_BASE}/${functionName}`;
  logger.debug(`Calling Edge Function: ${functionName}`, "api", { url });

  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const errorMessage =
      errorData.error || `HTTP ${response.status}: ${response.statusText}`;
    logger.error(`Edge Function error: ${functionName}`, "api", {
      status: response.status,
      error: errorMessage,
    });
    throw new Error(errorMessage);
  }

  const result = await response.json();
  logger.debug(`Edge Function success: ${functionName}`, "api", { result });
  return result as T;
}

/**
 * Consume AI credit for current user
 */
export async function consumeAiCredit(): Promise<{
  success: boolean;
  credits_remaining: number;
  plan: string;
  message: string;
}> {
  return callEdgeFunction("consume-ai-credit", {});
}

/**
 * Consume credits
 */
export async function consumeCredits(amount: number): Promise<{
  success: boolean;
  message: string;
}> {
  return callEdgeFunction("consume-credits", { amount });
}

/**
 * Consume quota credits
 */
export async function consumeQuotaCredits(
  action: string,
  credits: number,
): Promise<{
  success: boolean;
  message: string;
}> {
  return callEdgeFunction("consume-quota-credits", { action, credits });
}

/**
 * Redeem beta key
 */
export async function redeemBetaKey(code: string): Promise<{
  success: boolean;
  error?: string;
  tier?: string;
  credits?: number;
  expires_at?: string;
}> {
  return callEdgeFunction("redeem-beta-key", { code });
}

/**
 * Create poll (supports both authenticated and guest creation)
 */
export async function createPoll(params: {
  type: string;
  title: string;
  slug: string;
  poll_data_json: unknown;
  description?: string;
  guest_fingerprint?: string;
  guest_session_id?: string;
  guest_owner_token?: string;
  status?: string;
}): Promise<unknown> {
  const token = await getAuthToken();
  const isGuest = params.guest_fingerprint && !token;

  if (!token && !isGuest) {
    throw new Error("Authentication required for this operation");
  }

  const url = `${EDGE_FUNCTION_BASE}/create-poll`;
  logger.debug(`Calling Edge Function: create-poll`, "api", { url, isGuest });

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  // Sécurité #828 : pour une création invité, joindre le jeton de possession
  // (256 bits, localStorage). Sans lui, le poll retombe sur le contrôle
  // fingerprint forgeable → IDOR. Le serveur n'en stocke que le hash SHA-256.
  // (Régression : le wrapper Edge #859 ne transmettait pas ce champ, si bien
  //  que 100 % des polls invités étaient créés sans jeton.)
  const body: Record<string, unknown> = { ...params };
  if (isGuest && !params.guest_owner_token) {
    const ownerToken = getGuestOwnerToken();
    if (ownerToken) body.guest_owner_token = ownerToken;
  }

  const response = await fetch(url, {
    method: "POST",
    headers,
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const errorMessage =
      errorData.error || `HTTP ${response.status}: ${response.statusText}`;
    logger.error(`Edge Function error: create-poll`, "api", {
      status: response.status,
      error: errorMessage,
    });
    throw new Error(errorMessage);
  }

  const result = await response.json();
  logger.debug(`Edge Function success: create-poll`, "api", { result });
  return result;
}

/**
 * Submit poll response
 */
export async function submitPollResponse(params: {
  poll_id: string;
  response_data: unknown;
  voter_name?: string;
  email?: string;
  response_type?: string;
  fingerprint?: string;
}): Promise<unknown> {
  return callEdgeFunction("submit-poll-response", params);
}

/**
 * Delete poll response
 */
export async function deletePollResponse(params: {
  response_id: string;
  fingerprint?: string;
}): Promise<unknown> {
  return callEdgeFunction("delete-poll-response", params);
}

// DEAD CODE (2026-06-18) — aucun appelant dans le repo. Le claim invité→user
// au login passe par PollService.migrateGuestPollsToUser, qui appelle la RPC
// claim_guest_polls EN DIRECT avec p_owner_token (jeton #828). Cet ancien
// wrapper Edge ne transmet QUE le fingerprint forgeable (pas de jeton) : le
// réactiver rouvrirait l'IDOR fermé par #828. Conservé commenté pour mémoire ;
// supprimer (avec la fonction Edge claim-guest-polls) après confirmation.
// export async function claimGuestPolls(fingerprint: string): Promise<unknown> {
//   return callEdgeFunction("claim-guest-polls", { fingerprint });
// }

/**
 * Verify private poll access
 */
export async function verifyPrivatePollAccess(params: {
  poll_id: string;
  email?: string;
}): Promise<unknown> {
  return callEdgeFunction("verify-private-poll-access", params);
}
